/* eslint-disable react/prop-types */
import CloseIcon from 'Assets/images/close-popup.svg';
import CalendarIcon from 'Assets/images/commons/calendar.svg';
import CkEditorField from 'Components/Form/CkEditorField/CkEditorField';
import { DEFAULT_DOCUMENT_FILE_TYPES_UPLOAD } from 'Constants/commons';
import useIsHealthSummaryUploading from 'Hooks/files/useIsHealthSummaryUploading';
import useHover from 'Hooks/useHover';
import { InputField, SingleDatePickerField, TextAreaField } from 'Modules/Formik';
import FileUploadField from 'Modules/Formik/FileUploadField/FileUploadField';
import Space from 'Modules/Space';
import { fileUploadURL, healthSummaryFileUpload } from 'Redux/modules/files/actions';
import {
  getSummaryConfig,
  healthSummaryReset,
  healthSummaryRetrieve,
  healthSummaryUpdate,
} from 'Redux/modules/webChat/healthSummary/actions';
import { getHealthSummaryById } from 'Selectors/webChat/healthSummary';
import { Field, Formik } from 'formik';
import moment from 'moment';
import { bool, objectOf, shape } from 'prop-types';
import { useCallback, useEffect } from 'react';
import { ANCHOR_RIGHT, OPEN_UP } from 'react-dates/constants';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'semantic-ui-react';
import { updateEntities } from 'Redux/modules/entities/actions';
import { WEBCHAT } from 'Constants/normalize-module';

import { MARKDOWN_EDITOR_PLUGINS } from '../../AINotes/editorConfig';
import { SummarySchema } from '../validation';

import styles from './SummaryContent.less';

const TITLE_SUMMARY = 'Consult Summary';
const DATE_FORMAT = 'MMM DD, YYYY';

const wrapStyles = { marginBottom: 8, maxHeight: 320 };

const useSummaryConfig = () => {
  const dispatch = useDispatch();
  const configs = useSelector(state => state.webChat.healthSummary.configs);
  useEffect(() => {
    dispatch(getSummaryConfig());
  }, [dispatch]);
  return configs;
};

const handleMarkdownOutput = data => {
  // Replace _ with * for italic formatting
  // Replace \n with '  \n' for break line
  // Replace unordered list markers * with - but not conflict with italic formatting
  const listPattern = /(^|\n)\s*\*\s+/g;
  const newData = data
    .replace(listPattern, '$1- ')
    .replace(/_(.*?)_/g, '*$1*')
    .replace(/\n/g, '  \n');
  return newData;
};

const useSummary = healthSummaries => {
  const dispatch = useDispatch();
  const healthSummaryId = healthSummaries[0]?.healthSummaryId;
  const doctorId = healthSummaries[0]?.doctorId;
  // }
  const summary = useSelector(state => getHealthSummaryById(state, healthSummaryId));

  useEffect(() => {
    if (healthSummaryId) dispatch(healthSummaryRetrieve({ doctorId, summaryId: healthSummaryId }));
  }, [dispatch, doctorId, healthSummaryId]);

  return {
    summary,
  };
};

const genInitialValues = (defaultTitle, summaryObj, configs) => {
  const { visitDoctorIf: visitDoctorIfList = [] } = configs;
  if (!!summaryObj && visitDoctorIfList.length) {
    const { title, summary, clinicalNotes, plan, privateNote, followUpDate, attachments } =
      summaryObj;

    const initialValues = {
      title,
      plan,
      privateNote,
      clinicalNotes,
      attachments: attachments ?? [],
      followUpDate: moment(followUpDate).isValid() ? moment(followUpDate) : null,
      summary,
      status: 'draft',
    };

    return initialValues;
  }

  return {
    title: defaultTitle,
    clinicalNotes: '',
    summary: '',
    plan: '',
    privateNote: '',
    followUpDate: null,
    attachments: [],
  };
};

const HEALTH_SUMMARY_STATUS = {
  DRAFT: 'draft',
  FINAL: 'final',
};

const SummaryReviewForm = props => {
  const {
    doctor: { id: doctorId },
    consult,
    enable,
    formRef,
  } = props;
  const dispatch = useDispatch();
  const { healthSummary = [], createdAt } = consult;

  const configs = useSummaryConfig();
  const { summary } = useSummary(healthSummary);
  const defaultTitle = `${TITLE_SUMMARY} - ${moment(createdAt).format(DATE_FORMAT)}`;
  const initialValues = genInitialValues(defaultTitle, summary, configs);
  const [hoverRef, isHovered] = useHover();

  const { isHealthSummaryUploading, resetHealthSummaryUpload, showAlert } =
    useIsHealthSummaryUploading();

  const onSubmit = async values => {
    if (values.status === HEALTH_SUMMARY_STATUS.FINAL && isHealthSummaryUploading) {
      showAlert();
      return;
    }

    const apiBody = {
      title: values.title,
      clinicalNotes: handleMarkdownOutput(values.clinicalNotes?.trim() ?? ''),
      summary: values.summary ?? '',
      plan: handleMarkdownOutput(values.plan?.trim() ?? ''),
      privateNote: values.privateNote?.trim() ?? '',
      status: values.status,
      followUpDate: moment(values.followUpDate).isValid()
        ? values.followUpDate.format('YYYY-MM-DD')
        : undefined,
      // only pass success files
      attachments: values.attachments.filter(file => file.uploadStatus === 'success'),
      // final status use when finish consult, backend need it to validation mandatory fields
    };

    const data = await dispatch(
      healthSummaryUpdate({ doctorId, healthSummaryId: summary.id, body: apiBody }),
    ); // NOSONAR

    dispatch(updateEntities(WEBCHAT, 'healthSummaries', summary.id, data?.data));
  };

  const isNeedRequireMandatoryField = true;

  const gennarateLabel = useCallback(
    label => `${label} ${isNeedRequireMandatoryField ? '(*)' : ''}`,
    [isNeedRequireMandatoryField],
  );

  useEffect(() => {
    resetHealthSummaryUpload();
    return () => dispatch(healthSummaryReset());
  }, [dispatch, resetHealthSummaryUpload]);

  const handleSelectedFile = (_newValue, listValue) => {
    dispatch(healthSummaryFileUpload(listValue));
  };

  return (
    <div className={styles.formWrapper}>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        innerRef={formRef}
        validationSchema={SummarySchema}>
        {({ handleSubmit, values }) => (
          <Form onSubmit={handleSubmit}>
            <Field
              label={gennarateLabel('Title')}
              name="title"
              component={InputField}
              placeholder="Title"
              disabled={!enable}
            />
            <CkEditorField
              name="clinicalNotes"
              label={
                <Space size={4}>
                  <span>{gennarateLabel('Clinical Notes')}</span>
                  <span className={styles.noteSubLabel}>
                    <i>Patient will not see this note.</i>
                  </span>
                </Space>
              }
              wrapStyles={wrapStyles}
              plugins={MARKDOWN_EDITOR_PLUGINS}
              disabled={!enable}
            />
            <Field
              label={gennarateLabel('Diagnosis')}
              name="summary"
              component={InputField}
              placeholder="Diagnosis"
              disabled={!enable}
            />
            <CkEditorField
              name="plan"
              label={gennarateLabel('Plan')}
              wrapStyles={wrapStyles}
              plugins={MARKDOWN_EDITOR_PLUGINS}
              disabled={!enable}
            />

            <Space size={4}>
              <span className={styles.followUpVisitLabel}>
                Follow up visit <span className={styles.noteSubLabel}>(optional)</span>
              </span>
            </Space>

            <Field
              name="followUpDate"
              component={SingleDatePickerField}
              monthPick
              readOnly
              showClearDate={!!values.followUpDate}
              anchorDirection={ANCHOR_RIGHT}
              openDirection={OPEN_UP}
              customCloseIcon={
                <img
                  className={styles.calendarIcon}
                  ref={hoverRef}
                  src={isHovered ? CloseIcon : CalendarIcon}
                  alt="close popup"
                />
              }
              customInputIcon={
                !values.followUpDate ? (
                  <img className={styles.calendarIcon} src={CalendarIcon} alt="calendar" />
                ) : null
              }
              isOutsideRange={day => day.isBefore(moment().subtract(1, 'day'))}
              disabled={!enable}
            />

            <Field
              label={
                <Space size={4}>
                  <span>
                    Note <span className={styles.noteSubLabel}>(optional)</span>
                  </span>
                  <span className={styles.noteSubLabel}>
                    <i>This is your private note. Patient will not see it.</i>
                  </span>
                </Space>
              }
              name="privateNote"
              component={TextAreaField}
              placeholder="Note"
              disabled={!enable}
            />
            <Field
              name="attachments"
              component={FileUploadField}
              onFileSelected={handleSelectedFile}
              maxFile={1000}
              allowFileType={DEFAULT_DOCUMENT_FILE_TYPES_UPLOAD}
              uploadAction={fileUploadURL}
              multiple
              isPublic={false}
              disabled={!enable}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

SummaryReviewForm.propTypes = {
  doctor: shape({}).isRequired,
  consult: objectOf(shape({})).isRequired,
  enable: bool,
  formRef: shape({}).isRequired,
};

SummaryReviewForm.defaultProps = {
  enable: false,
};

export default SummaryReviewForm;
